import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { kk, ru } from "date-fns/locale";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import DocumentsService from "../../common/api/documentsApi";
import { MarkMovementItem } from "../../common/api/marksRegistryApi";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import style from "./MarkingCodes.module.css";

type Props = {
  items: MarkMovementItem[];
  loading?: boolean;
  /** Номер заказа с карточки КМ — fallback для Эмиссия/Нанесение */
  orderNumber?: string | null;
};

function parseEventDate(eventAt?: string): Date | null {
  if (!eventAt) return null;
  const m = eventAt.match(
    /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/,
  );
  if (m) {
    const [, dd, mm, yyyy, hh = "0", mi = "0", ss = "0"] = m;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss),
    );
  }
  const d = new Date(eventAt);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Через date-fns, а не Intl: у браузеров нет казахских данных для Intl, и kk-KZ давал «2025 M01 8»
function formatDateHeader(date: Date, language: string): string {
  const locale = normalizedLocale(language) === "kk" ? kk : ru;
  return format(date, "d MMMM yyyy", { locale });
}

// Названия событий и документов, которые бэк отдаёт только по-русски
const UNTRANSLATED_NAME_KEYS: Record<string, string> = {
  "персонализация": "marking_codes.event_personalization",
  "агрегация": "marking_codes.event_aggregation",
  "разагрегация": "marking_codes.event_disaggregation",
};

function localizeName(name: string | null | undefined, t: (key: string) => string) {
  if (!name) return name;
  const key = UNTRANSLATED_NAME_KEYS[name.trim().toLowerCase()];
  return key ? t(key) : name;
}

function formatTime(eventAt?: string): string {
  if (!eventAt) return "";
  const m = eventAt.match(/\s+(\d{2}:\d{2}:\d{2})/);
  if (m) return m[1];
  const d = parseEventDate(eventAt);
  if (!d) return eventAt;
  return d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function partyLabel(name?: string | null, iinBin?: string | null) {
  if (name && iinBin) return `${name} (${iinBin})`;
  return name || iinBin || "";
}

function isEmissionOrInflictionEvent(eventName?: string | null) {
  const name = String(eventName || "").toLowerCase();
  if (!name) return false;
  return (
    name.includes("эмисс") ||
    name.includes("нанес") ||
    name.includes("issued") ||
    name.includes("inflict") ||
    name.includes("emission")
  );
}

function documentPathByRefType(
  refDocType: string | number | undefined,
  documentId: number,
): string | null {
  const type = String(refDocType ?? "");
  if (type === "1") return `/documents/import-other-countries/${documentId}`;
  if (type === "2") return `/documents/inventory-write-off/${documentId}`;
  if (type === "3") return `/documents/inventory-resumption/${documentId}`;
  if (type === "4") return `/documents/import-eaeu/${documentId}`;
  if (type === "5") return `/documents/transfer-acceptance/${documentId}`;
  if (type === "6") return `/documents/personalization/${documentId}`;
  if (type === "7") return `/documents/aggregation/${documentId}/orders`;
  return null;
}

export default function MarkMovementTab({
  items,
  loading,
  orderNumber: cardOrderNumber,
}: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  const groups = useMemo(() => {
    const sorted = [...(items || [])].sort((a, b) => {
      const da = parseEventDate(a.eventAt)?.getTime() ?? 0;
      const db = parseEventDate(b.eventAt)?.getTime() ?? 0;
      return db - da;
    });

    const map = new Map<string, { label: string; events: MarkMovementItem[] }>();
    for (const item of sorted) {
      const d = parseEventDate(item.eventAt);
      const key = d ? dateKey(d) : "unknown";
      const label = d
        ? formatDateHeader(d, i18n.language)
        : t("marking_codes.unknown_date");
      if (!map.has(key)) map.set(key, { label, events: [] });
      map.get(key)!.events.push(item);
    }
    return Array.from(map.values());
  }, [items, i18n.language, t]);

  const openDocument = async (documentId: number) => {
    setOpeningId(documentId);
    setOpenError(null);
    try {
      const res = await DocumentsService.getDocument(String(documentId));
      const data = res?.data || res || {};
      const path = documentPathByRefType(data.refDocType, documentId);
      if (path) {
        navigate(path);
      } else {
        setOpenError(t("marking_codes.document_open_error"));
      }
    } catch {
      setOpenError(t("marking_codes.document_open_error"));
    } finally {
      setOpeningId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-10 text-center text-gray-500">
        {t("marking_codes.loading")}
      </div>
    );
  }

  if (!items?.length) {
    return (
      <EmptyListPlaceholderContent
        title={t("marking_codes.no_movement_title")}
        description={t("marking_codes.no_movement_description")}
      />
    );
  }

  return (
    <div className={style.movementRoot}>
      {openError ? (
        <p className="mb-3 text-sm text-red-600">{openError}</p>
      ) : null}
      {groups.map((group) => (
        <div key={group.label} className={style.movementGroup}>
          <div className={style.movementDate}>{group.label}</div>
          <div className={style.movementList}>
            {group.events.map((event, index) => {
              const hasDoc =
                event.documentId != null ||
                event.documentName ||
                event.documentNumber;
              const isOrderEvent = isEmissionOrInflictionEvent(event.eventName);
              const orderNumber =
                event.orderNumber ||
                (isOrderEvent ? cardOrderNumber : null) ||
                null;
              const hasOrder = !!orderNumber;
              const hasSender = !!(event.senderName || event.sender);
              const hasReceiver = !!(event.receiverName || event.receiver);
              const hasCheck =
                event.checkId != null ||
                event.fiscalDocumentNumber != null ||
                !!event.checkType;

              return (
                <div
                  key={`${event.eventAt}-${event.eventName}-${index}`}
                  className={style.movementRow}
                >
                  <div className={style.movementEvent}>
                    <div className={style.movementEventIcon} aria-hidden>
                      <img
                        src="/icons/datamatrix.svg"
                        alt=""
                        className="w-5 h-5"
                      />
                    </div>
                    <div>
                      <div className={style.movementEventName}>
                        {localizeName(event.eventName, t) || "—"}
                      </div>
                      <div className={style.movementEventTime}>
                        {formatTime(event.eventAt)}
                      </div>
                    </div>
                  </div>

                  <div className={style.movementParties}>
                    {hasSender ? (
                      <div className={style.movementParty}>
                        <div className={style.movementLabel}>
                          {t("marking_codes.sender")}
                        </div>
                        <div className={style.movementValue}>
                          {partyLabel(event.senderName, event.sender)}
                        </div>
                      </div>
                    ) : null}
                    {hasReceiver ? (
                      <div className={style.movementParty}>
                        <div className={style.movementLabel}>
                          {t("marking_codes.receiver")}
                        </div>
                        <div className={style.movementValue}>
                          {partyLabel(event.receiverName, event.receiver)}
                        </div>
                      </div>
                    ) : null}
                    {!hasSender && !hasReceiver && (
                      <div className={style.movementValueMuted}>—</div>
                    )}
                  </div>

                  <div className={style.movementDoc}>
                    {hasOrder ? (
                      <div
                        className={
                          hasDoc || hasCheck ? "mb-3" : undefined
                        }
                      >
                        <div className={style.movementLabel}>
                          {t("marking_codes.order_number")}
                        </div>
                        {event.orderId != null ? (
                          <button
                            type="button"
                            className={style.movementDocLinkClickable}
                            onClick={() =>
                              navigate(`/orders/${event.orderId}`)
                            }
                          >
                            {orderNumber}
                          </button>
                        ) : (
                          <div className={style.movementDocLink}>
                            {orderNumber}
                          </div>
                        )}
                      </div>
                    ) : null}
                    {hasDoc ? (
                      <div className={hasCheck ? "mb-3" : undefined}>
                        <div className={style.movementLabel}>
                          {localizeName(event.documentName, t) || t("marking_codes.document")}
                        </div>
                        {event.documentId != null ? (
                          <button
                            type="button"
                            className={style.movementDocLinkClickable}
                            disabled={openingId === event.documentId}
                            onClick={() =>
                              openDocument(Number(event.documentId))
                            }
                          >
                            {event.documentNumber || String(event.documentId)}
                          </button>
                        ) : (
                          <div className={style.movementDocLink}>
                            {event.documentNumber || "—"}
                          </div>
                        )}
                      </div>
                    ) : null}
                    {hasCheck ? (
                      <div className={style.movementParties}>
                        {event.checkType ? (
                          <div className={style.movementParty}>
                            <div className={style.movementLabel}>
                              {t("marking_codes.check_type")}
                            </div>
                            <div className={style.movementValue}>
                              {event.checkType}
                            </div>
                          </div>
                        ) : null}
                        {event.fiscalDocumentNumber != null &&
                        event.fiscalDocumentNumber !== "" ? (
                          <div className={style.movementParty}>
                            <div className={style.movementLabel}>
                              {t("marking_codes.fiscal_document_number")}
                            </div>
                            <div className={style.movementValue}>
                              {String(event.fiscalDocumentNumber)}
                            </div>
                          </div>
                        ) : null}
                        {event.checkId != null ? (
                          <div className={style.movementParty}>
                            <div className={style.movementLabel}>
                              {t("marking_codes.check_id")}
                            </div>
                            <button
                              type="button"
                              className={style.movementDocLinkClickable}
                              onClick={() =>
                                navigate(`/cheque/${event.checkId}`)
                              }
                            >
                              {String(event.checkId)}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {!hasOrder && !hasDoc && !hasCheck ? (
                      <div className={style.movementValueMuted}>—</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
